import { useMutation } from '@apollo/client';
import { useRouter, useShowToast } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  NewsletterCreateEmailContactMutation,
  NewsletterCreateEmailContactMutationVariables,
} from '@gql/graphql';
import type { PromoNewsletterFormValues } from '@presentation/components';
import { useCallback } from 'react';

export const NEWSLETTER_CREATE_EMAIL_CONTACT = graphql(`
  mutation NewsletterCreateEmailContact($data: CreateEmailContactInput!) {
    createEmailContact(data: $data) {
      id
    }
  }
`);

export interface UseCreateEmailContactParams {
  shouldShowToast?: boolean;
}

export const useCreateEmailContact = (params?: UseCreateEmailContactParams) => {
  const { shouldShowToast } = params ?? { shouldShowToast: true };

  const { language, translate, countryCode } = useRouter();
  const { showToast } = useShowToast();

  const [send, { loading, error }] = useMutation<
    NewsletterCreateEmailContactMutation,
    NewsletterCreateEmailContactMutationVariables
  >(NEWSLETTER_CREATE_EMAIL_CONTACT);

  const showSuccessToast = useCallback(() => {
    showToast(translate('common.toasts.newsletterSuccess'), {
      type: 'success',
      autoClose: 2500,
    });
  }, [showToast, translate]);

  const createEmailContactFromForm = useCallback(
    async (values: PromoNewsletterFormValues) => {
      const { email, marketingEmailConsent, hasADog, createdFrom } = values;

      await send({
        variables: {
          data: {
            email,
            language,
            hasTakenBreedTest: false,
            country: countryCode,
            marketingEmailConsent,
            createdFrom,
            hasADog,
          },
        },
      });
      if (shouldShowToast) {
        showSuccessToast();
      }
    },
    [language, send, showSuccessToast, countryCode, shouldShowToast],
  );

  return {
    createEmailContactFromForm,
    createEmailContactLoading: loading,
    createEmailContactError: error,
  };
};
